import AppForm from '../app-components/Form/AppForm';

// the tab opened the last time, shared by all the contract types
const ACTIVE_TAB_STORAGE_KEY = 'contract-type-form-active-tab';

Vue.component('contract-type-form', {
    mixins: [AppForm],
    props: {
        // groups of the dynamic additions: [{ key, label, additions: { settingName: { label, ... } } }]
        'dynamicAdditionGroups': {
            type: Array,
            default: function() {
                return [];
            },
        },
        // labels of the interval types: { intervalType: label }
        'intervalTypeLabels': {
            type: Object,
            default: function() {
                return {};
            },
        },
    },
    data: function() {
        return {
            activeTab: this.loadActiveTab(),
            activeSettingGroup: null,
            settingsNavStyle: {},
            settingGroupLockedUntil: 0,
            intervalSearch: '',
            additionSearch: '',
            additionFilter: 'all',
            initialFormState: null,
            footerStyle: {},
            footerPinned: false,
            footerResizeObserver: null,
            form: {
                title:  null,
                shortcode: null,
                mymcd_contract_type_id: null,
                company_contract_type_id: null,
                restaurant_id: null,
                settings: {},
                intervalTypeSettings: {},
                legalChecks: {},
            }
        }
    },

    mounted: function() {
        // the snapshot is taken once the form is filled with the data of the contract type
        this.$nextTick(() => {
            this.initialFormState = this.serializeFormState();
            this.updateFooterPosition();
            this.updateActiveSettingGroup();
        });

        // the footer with the save button sits right below the form; when its place is below the bottom
        // of the window (long tab), it is pinned to the bottom of the window so it stays visible
        window.addEventListener('resize', this.updateFooterPosition);
        window.addEventListener('resize', this.updateActiveSettingGroup);
        document.addEventListener('scroll', this.updateFooterPosition, true);
        document.addEventListener('scroll', this.updateActiveSettingGroup, true);
        document.addEventListener('wheel', this.unlockActiveSettingGroup, { passive: true });
        document.addEventListener('touchmove', this.unlockActiveSettingGroup, { passive: true });
        if (window.ResizeObserver) {
            this.footerResizeObserver = new ResizeObserver(this.updateFooterPosition);
            this.footerResizeObserver.observe(this.$el);
        }
    },

    beforeDestroy: function() {
        window.removeEventListener('resize', this.updateFooterPosition);
        window.removeEventListener('resize', this.updateActiveSettingGroup);
        document.removeEventListener('scroll', this.updateFooterPosition, true);
        document.removeEventListener('scroll', this.updateActiveSettingGroup, true);
        document.removeEventListener('wheel', this.unlockActiveSettingGroup);
        document.removeEventListener('touchmove', this.unlockActiveSettingGroup);
        if (this.footerResizeObserver) {
            this.footerResizeObserver.disconnect();
        }
    },

    computed: {
        additionLabels()
        {
            let labels = {};
            this.dynamicAdditionGroups.forEach(group => {
                Object.keys(group.additions).forEach(key => {
                    labels[key] = this.normalizeSearchText(group.additions[key].label);
                });
            });

            return labels;
        },

        additionsTotal()
        {
            return Object.keys(this.additionLabels).length;
        },

        enabledAdditionsCount()
        {
            return Object.keys(this.additionLabels).filter(key => this.isAdditionEnabled(key)).length;
        },

        hasVisibleAdditions()
        {
            return Object.keys(this.additionLabels).some(key => this.isAdditionVisible(key));
        },

        hasVisibleIntervals()
        {
            return Object.keys(this.intervalTypeLabels).some(intervalType => this.isIntervalVisible(intervalType));
        },

        hasUnsavedChanges()
        {
            return this.initialFormState !== null && this.initialFormState !== this.serializeFormState();
        },
    },

    watch: {
        activeTab: function(newVal) {
            try {
                localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, newVal);
            } catch (e) {
                // the storage is not available (private window), the tab is just not remembered
            }

            this.$nextTick(() => {
                this.updateFooterPosition();
                this.updateActiveSettingGroup();
            });
        },

        // The whole create form (mymcd contract types, company contract types, legal checks) depends
        // on the selected restaurant, so it is re-rendered for it. The restaurant is then submitted
        // from the form itself, not read from the current restaurant on the server.
        'form.restaurant_id': function(newVal, oldVal) {
            if (this.data && this.data.id) {
                return;
            }

            if (newVal == null || oldVal == null || newVal === oldVal) {
                return;
            }

            // stay on the same page (create or clone), only for the newly selected restaurant
            window.location.href = window.location.pathname + '?restaurant_id=' + newVal;
        }
    },

    methods: {
        loadActiveTab()
        {
            let savedTab = null;
            try {
                savedTab = parseInt(localStorage.getItem(ACTIVE_TAB_STORAGE_KEY), 10);
            } catch (e) {
                // the storage is not available (private window)
            }

            return savedTab > 0 ? savedTab : 0;
        },

        updateFooterPosition()
        {
            // the holder keeps the place of the footer in the settings card even when the footer is pinned
            let holder = this.$refs.footerHolder;
            let card = holder.parentNode.getBoundingClientRect();

            this.footerPinned = holder.getBoundingClientRect().bottom > window.innerHeight;
            this.footerStyle = this.footerPinned
                ? { left: card.left + 'px', width: card.width + 'px' }
                : {};
        },

        settingGroupElements()
        {
            return Array.from(this.$el.querySelectorAll('[data-setting-group]'));
        },

        scrollToSettingGroup(groupKey, $event)
        {
            if ($event && $event.currentTarget) {
                $event.currentTarget.blur();
            }

            let group = this.settingGroupElements().find(element => element.dataset.settingGroup === groupKey);
            if (!group) {
                return;
            }

            // a short last section never reaches the top of the window, so the clicked one stays
            // highlighted until the scrolling ends
            this.activeSettingGroup = groupKey;
            this.settingGroupLockedUntil = Date.now() + 1000;
            group.scrollIntoView({ behavior: 'smooth', block: 'start' });
        },

        // keeps the navigation of the settings below the header of the page while its column is scrolled away
        updateSettingsNavPosition()
        {
            let nav = this.$refs.settingsNav;
            let list = this.$refs.settingsNavList;
            if (!nav || !list || nav.offsetParent === null) {
                this.settingsNavStyle = {};
                return;
            }

            let header = document.querySelector('.app-header');
            let headerRect = header ? header.getBoundingClientRect() : null;
            let offset = header && window.getComputedStyle(header).position === 'fixed' ? headerRect.bottom : 0;

            let column = nav.getBoundingClientRect();
            if (column.top >= offset) {
                this.settingsNavStyle = {};
                return;
            }

            // stops at the end of its column
            this.settingsNavStyle = {
                position: 'fixed',
                top: Math.min(offset, column.bottom - list.offsetHeight) + 'px',
                left: column.left + 'px',
                width: nav.clientWidth + 'px',
            };
        },

        // the user scrolls on their own again, the highlighted section follows the scrolling right away
        unlockActiveSettingGroup()
        {
            this.settingGroupLockedUntil = 0;
        },

        // highlights the section of the settings that is currently being read
        updateActiveSettingGroup()
        {
            this.updateSettingsNavPosition();

            if (Date.now() < this.settingGroupLockedUntil) {
                return;
            }

            // the tab with the settings is hidden
            let groups = this.settingGroupElements().filter(element => element.offsetParent !== null);
            if (groups.length === 0) {
                return;
            }

            // a section is active once its top passes the line. The line moves from the upper part of the window
            // down to its bottom as the form is scrolled to its end, so the short sections at the end, that
            // never get to the top of the window, take their turn too.
            let lineStart = window.innerHeight * 0.3;
            let holder = this.$refs.footerHolder;
            let scrolled = Math.max(0, lineStart - groups[0].getBoundingClientRect().top);
            let remaining = holder ? Math.max(0, holder.getBoundingClientRect().bottom - window.innerHeight) : 0;
            let progress = scrolled + remaining > 0 ? scrolled / (scrolled + remaining) : 0;
            let line = lineStart + progress * (window.innerHeight - lineStart);

            let active = groups[0];
            groups.forEach(element => {
                if (element.getBoundingClientRect().top <= line) {
                    active = element;
                }
            });

            this.activeSettingGroup = active.dataset.settingGroup;
        },

        isAdditionEnabled(key)
        {
            return parseInt(this.form.settings[key], 10) > 0;
        },

        isAdditionVisible(key)
        {
            if (this.additionFilter === 'enabled' && !this.isAdditionEnabled(key)) {
                return false;
            }

            if (this.additionFilter === 'disabled' && this.isAdditionEnabled(key)) {
                return false;
            }

            let search = this.normalizeSearchText(this.additionSearch).trim();

            return search === '' || (this.additionLabels[key] || '').indexOf(search) !== -1;
        },

        isAdditionGroupVisible(groupKey)
        {
            return this.additionGroupKeys(groupKey).some(key => this.isAdditionVisible(key));
        },

        enabledAdditionsInGroup(groupKey)
        {
            return this.additionGroupKeys(groupKey).filter(key => this.isAdditionEnabled(key)).length;
        },

        additionGroupKeys(groupKey)
        {
            let group = this.dynamicAdditionGroups.find(group => group.key === groupKey);

            return group ? Object.keys(group.additions) : [];
        },

        isIntervalVisible(intervalType)
        {
            let search = this.normalizeSearchText(this.intervalSearch).trim();
            let label = this.normalizeSearchText(this.intervalTypeLabels[intervalType]);

            return search === '' || label.indexOf(search) !== -1;
        },

        areAllIntervalsEnabled(column)
        {
            return Object.keys(this.form.intervalTypeSettings)
                .every(intervalType => this.isSwitchedOn(this.form.intervalTypeSettings[intervalType][column]));
        },

        // switches the whole column on, or off when everything in it is already switched on
        toggleAllIntervals(column)
        {
            let value = this.areAllIntervalsEnabled(column) ? false : '1';

            Object.keys(this.form.intervalTypeSettings).forEach(intervalType => {
                this.form.intervalTypeSettings[intervalType][column] = value;
            });
        },

        isLegalCheckActive(type)
        {
            return this.isSwitchedOn(this.form.legalChecks[type]);
        },

        activeLegalChecksCount(types)
        {
            return types.filter(type => this.isLegalCheckActive(type)).length;
        },

        // checkboxes hold 1, "1" or true when checked and 0 or false when not
        isSwitchedOn(value)
        {
            return Number(value) > 0;
        },

        // lower case without diacritics, so "priplatek" finds "Příplatek"
        normalizeSearchText(text)
        {
            return String(text || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        },

        // inputs turn numbers into strings (1 -> "1") and checkboxes into booleans, which is not a change
        serializeFormState()
        {
            return JSON.stringify(this.form, (key, value) => {
                return typeof value === 'number' || typeof value === 'boolean' ? String(Number(value)) : value;
            });
        },

        goBack()
        {
            window.location.href = '/contract-types';
        },

        sanitizeDecimalPoint(val)
        {
            return (val ? val.replace(",",".") : val);
        },

        sanitizeIncorrectFloatChars(val)
        {
            val = this.sanitizeDecimalPoint(val);
            return (val ? val.replace(/[^0-9.]/,"") : val);
        },

        correctBasicInputDecimals(vmodelName,$event)
        {
            this.form.settings[vmodelName] = this.sanitizeIncorrectFloatChars($event.target.value);
        }
    }
});



// WEBPACK FOOTER //
// ./resources/assets/admin/js/contract-type/Form.js